import { Map, MapModal, Marker } from "components/Map"
import {
  getArrivalCoords,
  getDepartureCoords,
  getStopoversCoords,
} from "./model"
import { useEffect, useMemo } from "react"
import { bbox } from "@turf/bbox"
import { lineString } from "@turf/helpers"
import * as PlaceType from "enums/PlaceType"
import { useMap } from "react-map-gl/mapbox"

export const AdvertMapModal = ({ advert, trigger }) => {
  const departureCoords = useMemo(() => getDepartureCoords(advert), [advert])
  const arrivalCoords = useMemo(
    () => (advert.arrivalType ? getArrivalCoords(advert) : null),
    [advert],
  )
  const stopoversCoords = useMemo(() => getStopoversCoords(advert), [advert])

  const points = useMemo(
    () => [departureCoords, ...stopoversCoords, arrivalCoords].filter(Boolean),
    [departureCoords, stopoversCoords, arrivalCoords],
  )

  if (points.length === 0) {
    return null
  }

  return (
    <MapModal
      title=""
      trigger={trigger}
      map={
        <Map
          initialViewState={{
            longitude: departureCoords?.lng,
            latitude: departureCoords?.lat,
            zoom: 5,
          }}
        >
          <ViewStateAdapter
            points={points}
            departureCoords={departureCoords}
            arrivalCoords={arrivalCoords}
            advert={advert}
          />
          {departureCoords ? (
            <Marker
              latitude={departureCoords.lat}
              longitude={departureCoords.lng}
              variant="departure"
            />
          ) : null}
          {arrivalCoords ? (
            <Marker
              latitude={arrivalCoords.lat}
              longitude={arrivalCoords.lng}
              variant="arrival"
            />
          ) : null}
          {stopoversCoords.map((stopover) => {
            return (
              <Marker
                key={`${stopover.lat};${stopover.lng}`}
                latitude={stopover.lat}
                longitude={stopover.lng}
                variant="stopover"
              />
            )
          })}
        </Map>
      }
    />
  )
}

const isSamePoint = (a, b) => {
  if (!a || !b) {
    return false
  }

  return a.lng === b.lng && a.lat === b.lat
}

const getZoomLevelForPlaceType = (placeType) => {
  switch (placeType) {
    case PlaceType.PORT:
      return 5

    case PlaceType.AREA_REGIONAL:
      return 5

    case PlaceType.AREA_GLOBAL:
      return 2

    default:
      throw new Error(`Unkown place type: ${placeType}`)
  }
}

const ViewStateAdapter = ({
  points,
  departureCoords,
  arrivalCoords,
  advert,
}) => {
  const map = useMap()

  useEffect(() => {
    if (
      points.length === 1 ||
      (isSamePoint(departureCoords, arrivalCoords) &&
        advert.stopovers.length === 0)
    ) {
      map.current?.setCenter([points[0].lng, points[0].lat])

      if (advert.departureType) {
        map.current?.setZoom(
          getZoomLevelForPlaceType(advert.departureType.enum_value),
        )
      }
    } else {
      const line = lineString(points.map((point) => [point.lng, point.lat]))
      const bounds = bbox(line)

      map.current?.fitBounds([bounds.slice(0, 2), bounds.slice(2)], {
        padding: 50,
      })
    }
  }, [
    advert.departureType,
    advert.stopovers.length,
    arrivalCoords,
    departureCoords,
    map,
    points,
  ])

  return null
}
